// Grill The Grid Page
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useGrillTheGrid } from '../../hooks/useGrillTheGrid';
import { useGrillTheGridEpisodes } from '../../hooks/useGrillTheGridEpisodes';
import { useF1Championship } from '../../hooks/useF1Championship';
import F1PageTemplate from '../../components/f1/F1PageTemplate';
import { TableChart } from '../../components/charts';
import GrillTheGridEpisodeSection from '../../components/f1/GrillTheGridEpisodeSection';

const GrillTheGridPage = () => {
  const { data, dataByYear, loading, error, availableYears, getStandingsByYear } = useGrillTheGrid();
  const { getEpisodesByYear } = useGrillTheGridEpisodes();
  const { data: f1Data } = useF1Championship();
  const [selectedYear, setSelectedYear] = useState(null);

  if (loading) {
    return (
      <div className="min-h-screen theme-bg-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-semibold theme-primary mb-4">Loading Grill The Grid Data...</div>
          <div className="theme-secondary">Please wait while we load the data</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen theme-bg-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl font-semibold text-red-600 mb-4">Error Loading Data</div>
          <div className="theme-secondary">{error}</div>
        </div>
      </div>
    );
  }

  const currentYear = selectedYear || (availableYears.length > 0 ? availableYears[0] : null);
  const standings = currentYear ? getStandingsByYear(currentYear) : [];
  const episodes = currentYear ? getEpisodesByYear(currentYear) : [];

  // Get driver name from driver ID
  const getDriverName = (driverId) => {
    const driver = f1Data?.drivers?.find(d => d.driverId == driverId);
    if (driver) {
      return `${driver.forename} ${driver.surname}`;
    }
    return `Driver ${driverId}`;
  };

  // Transform standings data for table display
  const tableData = standings.map(standing => {
    const row = {
      position: standing.position,
      driver: (
        <Link 
          to={`/f1/drivers/${standing.driverId}`}
          className="theme-primary hover:theme-accent hover:underline font-medium"
        >
          {getDriverName(standing.driverId)}
        </Link>
      ),
      totalPoints: standing.totalPoints
    };

    // Add round columns
    Object.keys(standing.rounds).sort((a, b) => a - b).forEach(round => {
      row[`Round ${round}`] = standing.rounds[round] || '-';
    });

    return row;
  });

  // Generate table columns dynamically
  const columns = [
    { key: 'position', label: 'Position' },
    { key: 'driver', label: 'Driver' },
    { key: 'totalPoints', label: 'Total Points' }
  ];

  if (standings.length > 0) {
    const maxRound = Math.max(...standings.flatMap(s => Object.keys(s.rounds).map(Number)));
    for (let i = 1; i <= maxRound; i++) {
      columns.push({ key: `Round ${i}`, label: `Round ${i}` });
    }
  }

  const stats = [
    [
      { value: availableYears.length, label: 'Available Seasons', group: 'Data Overview' },
      { value: standings.length, label: 'Drivers in Season', group: 'Data Overview' },
      { value: currentYear || 'N/A', label: 'Selected Season', group: 'Data Overview' }
    ]
  ];

  return (
    <F1PageTemplate
      title="Grill The Grid"
      subtitle="Driver Performance in Grid Position Challenges"
      summary={['Season standings', 'Round-by-round performance', 'Driver comparisons']}
      stats={stats}
    >
      <div className="space-y-8">
        {/* Year Selector */}
        {availableYears.length > 0 && (
          <div className="card">
            <h3 className="text-xl font-semibold theme-primary mb-4">Select Season</h3>
            <div className="flex flex-wrap gap-2">
              {availableYears.map(year => (
                <button
                  key={year}
                  onClick={() => setSelectedYear(year)}
                  className={`px-4 py-2 rounded-md transition-colors ${
                    currentYear === year
                      ? 'bg-blue-600 text-white'
                      : 'theme-bg theme-primary hover:bg-opacity-80'
                  }`}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Standings Table */}
        {currentYear && standings.length > 0 && (
          <TableChart
            data={tableData}
            title={`${currentYear} Grill The Grid Standings`}
            subtitle="Driver performance across all rounds"
            config={{
              columns,
              sortable: true,
              sortBy: 'totalPoints',
              sortDirection: 'desc',
              pagination: true,
              pageSize: 20
            }}
          />
        )}

        {/* Episodes Section */}
        {currentYear && episodes.length > 0 && (
          <GrillTheGridEpisodeSection
            title={`${currentYear} Episodes`}
            episodes={episodes}
          />
        )}

        {currentYear && standings.length === 0 && (
          <div className="card text-center py-8">
            <p className="theme-secondary">No data available for season {currentYear}</p>
          </div>
        )}

        {!currentYear && (
          <div className="card text-center py-8">
            <p className="theme-secondary">Select a season to view Grill The Grid standings</p>
          </div>
        )}
      </div>
    </F1PageTemplate>
  );
};

export default GrillTheGridPage;
